<template>
  <div class="app-container">
    <!-- 页面顶部 - 订单信息区 -->
    <div class="order-info">
      <div class="order-info__left">
        <p class="order-info__item">订单编号：{{ detail.orderNo }}</p>
        <p class="order-info__item">下单时间：{{ detail.createDate | parseTime('{y}-{m}-{d} {h}:{i}:{s}') }}</p>
        <p class="order-info__item">
          实付金额：<span class="order-info__price">¥{{ detail.receiptAmount | formatMoney }}</span>
        </p>
        <p class="order-info__item">售后次数：{{ afterSalesRecordList.length }}次</p>
        <p class="order-info__item">
          累计退款金额：<span class="order-info__price">¥{{ detail.actualRefundAmount | formatMoney }}</span>
        </p>
      </div>
      <div class="order-info__right">
        <span class="order-info__status">{{ detail.statusName }}</span>
      </div>
    </div>

    <!-- Tab 切换区 -->
    <el-tabs v-model="activeTab" class="after-sale-tabs">
      <el-tab-pane name="afterSales">
        <span slot="label">售后记录 ({{ afterSalesList.length }})</span>
        <el-table :data="afterSalesList" style="width: 100%" border>
          <el-table-column label="售后单号" prop="recordNo" width="180"></el-table-column>
          <el-table-column label="售后类型" prop="recordTypeName" width="120"></el-table-column>
          <el-table-column label="商品信息" min-width="300">
            <template slot-scope="scope">
              <div v-for="(item, index) in scope.row.detailList" :key="index" class="goods-info">
                <img :src="item.commodityUrl" class="goods-info__img" />
                <div class="goods-info__content">
                  <p class="goods-info__name">{{ item.name }}</p>
                  <p class="goods-info__spec">{{ item.specValue }}</p>
                </div>
              </div>
              <div v-if="hasDeliveryComp(scope.row)" class="goods-info">
                <div class="goods-info__content">
                  <p class="goods-info__name">退运费</p>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="数量" width="100">
            <template slot-scope="scope">
              <div v-for="(item, index) in scope.row.detailList" :key="index" class="goods-qty">
                {{ item.applyNum }}
              </div>
              <div v-if="hasDeliveryComp(scope.row)" class="goods-qty">-</div>
            </template>
          </el-table-column>
          <el-table-column label="金额" width="200">
            <template slot-scope="scope">
              <div v-for="(item, index) in scope.row.detailList" :key="index" class="goods-amount">
                <p>申请：¥{{ item.applyAmount | formatMoney }}</p>
                <!-- 待退款状态尚未发生退款, 实退展示 "-" -->
                <p v-if="isWaitRefund(scope.row)">实退：-</p>
                <p v-else>实退：¥{{ item.actualRefundAmount | formatMoney }}</p>
                <p v-if="item.actualVirtualCreditAmount > 0">
                  返利：¥{{ item.actualVirtualCreditAmount | formatMoney }}
                </p>
              </div>
              <div v-if="hasDeliveryComp(scope.row)" class="goods-amount">
                <p>申请：¥{{ scope.row.deliveryCompAmount | formatMoney }}</p>
                <p v-if="isWaitRefund(scope.row) || !hasActualDeliveryComp(scope.row)">实退：-</p>
                <p v-else>实退：¥{{ scope.row.actualDeliveryCompAmount | formatMoney }}</p>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="申请时间" width="180">
            <template slot-scope="scope">
              {{ scope.row.createDate | parseTime('{y}-{m}-{d} {h}:{i}:{s}') }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120">
            <template slot-scope="scope">
              <span :class="getStatusClass(scope.row, 'afterSales')">{{ scope.row.frontStatusName }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template slot-scope="scope">
              <el-button type="text" @click="viewAfterSaleDetail(scope.row)">查看详情</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane name="reissue">
        <span slot="label">补发记录 ({{ reissueList.length }})</span>
        <el-table :data="reissueList" style="width: 100%" border>
          <el-table-column label="补发单号" prop="recordNo" width="180"></el-table-column>
          <el-table-column label="补发类型" prop="recordTypeName" width="120"></el-table-column>
          <el-table-column label="商品信息" min-width="300">
            <template slot-scope="scope">
              <div v-for="(item, index) in scope.row.detailList" :key="index" class="goods-info">
                <img :src="item.commodityUrl" class="goods-info__img" />
                <div class="goods-info__content">
                  <p class="goods-info__name">{{ item.name }}</p>
                  <p class="goods-info__spec">{{ item.specValue }}</p>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="补发数量" width="100">
            <template slot-scope="scope">
              <div v-for="(item, index) in scope.row.detailList" :key="index" class="goods-qty">
                {{ item.applyNum }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="物流信息" width="200">
            <template slot-scope="scope">
              <template v-if="scope.row.orderDeliveryList && scope.row.orderDeliveryList.length > 0">
                <p>{{ scope.row.orderDeliveryList[0].companyName }}</p>
                <p>{{ scope.row.orderDeliveryList[0].deliveryNo }}</p>
              </template>
              <span v-else>/</span>
            </template>
          </el-table-column>
          <el-table-column label="发货时间" width="180">
            <template slot-scope="scope">
              {{ scope.row.deliveryTime | parseTime('{y}-{m}-{d} {h}:{i}:{s}') }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120">
            <template slot-scope="scope">
              <span :class="getStatusClass(scope.row, 'reissue')">{{ scope.row.frontStatusName }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template slot-scope="scope">
              <el-button type="text" @click="viewLogistics(scope.row)">查看物流</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <DeliveryListDialog
      :visible.sync="logisticsVisible"
      :order-no="logisticsOrderNo"
      :selected-order-no="logisticsSelectedOrderNo"
    />
  </div>
</template>

<script>
import { getById } from '@/api/order/inquiry/list';
import DeliveryListDialog from '@/components/DeliveryListDialog';

export default {
  name: 'AfterSaleProgress',
  components: {
    DeliveryListDialog
  },
  data() {
    return {
      detail: {},
      activeTab: 'afterSales',
      loading: false,
      logisticsVisible: false, // 查看物流弹窗
      logisticsOrderNo: '', // 物流弹窗查询用订单编号(原始订单)
      logisticsSelectedOrderNo: '' // 物流弹窗预选的补发订单编号
    };
  },
  computed: {
    afterSalesRecordList() {
      return this.detail.afterSalesRecordList || [];
    },
    afterSalesList() {
      return this.afterSalesRecordList.filter(
        item =>
          item.recordType === 'AFTER_SALES_APPLY' ||
          item.recordType === 'AFTER_SALES_ORDER' ||
          item.recordType === 'AFTER_SALES_HISTORY'
      );
    },
    reissueList() {
      return this.afterSalesRecordList.filter(
        item => item.recordType === 'REISSUE_APPLY' || item.recordType === 'REISSUE_ORDER'
      );
    }
  },
  created() {
    this.fetchDetail();
  },
  methods: {
    fetchDetail() {
      const id = this.$route.params.id;
      this.loading = true;
      getById(id)
        .then(response => {
          this.detail = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // 售后单是否处于待退款状态
    isWaitRefund(row) {
      return row.status === 'WAIT_REFUND';
    },
    // 是否存在申请退运费
    hasDeliveryComp(row) {
      return Number(row.deliveryCompAmount) > 0;
    },
    // 是否存在实际退运费
    hasActualDeliveryComp(row) {
      return Number(row.actualDeliveryCompAmount) > 0;
    },
    getStatusClass(row, type) {
      const { status } = row;
      if (type === 'afterSales') {
        // 待收货(DELIVERED) / 部分发货(SUB_DELIVERY)
        if (status === 'DELIVERED' || status === 'SUB_DELIVERY') return 'status-orange';
        // 售后完成(FINISHED)
        if (status === 'FINISHED') return 'status-red';
      } else {
        // 补发: 已发货(DELIVERED) / 已收货(RECEIVED)
        if (status === 'DELIVERED' || status === 'RECEIVED') return 'status-green';
      }
      return '';
    },
    viewAfterSaleDetail(row) {
      this.$router.push(`/syoung/order/after-sales/detail/APPLY/${row.id}`);
    },
    // 查看物流: 与订单详情一致使用 DeliveryListDialog, 并预选该补发记录对应的补发订单
    // 注意: row.orderNo 是原始订单号, row.recordNo 才是补发单号
    viewLogistics(row) {
      this.logisticsOrderNo = this.detail.orderNo || '';
      this.logisticsSelectedOrderNo = row.recordNo || '';
      this.logisticsVisible = true;
    }
  },
  filters: {
    formatMoney(value) {
      if (value === null || value === undefined) return '0.00';
      return Number(value).toFixed(2);
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.app-container {
  width: 1180px;
  color: #333;
  margin-bottom: 60px;
}

.order-info {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background-color: #f8f8f8;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e5e5e5;

  &__left {
    flex: 1;
  }

  &__item {
    line-height: 28px;
    font-size: 14px;
  }

  &__price {
    color: #ab0033;
    font-weight: bold;
  }

  &__right {
    text-align: right;
  }

  &__status {
    font-size: 18px;
    color: #ab0033;
    font-weight: bold;
  }
}

.after-sale-tabs {
  /deep/ .el-tabs__header {
    margin-bottom: 0;
  }

  /deep/ .el-tabs__item {
    font-size: 14px;
  }
}

// 三列(商品信息/数量/金额)按商品分块, 统一最小行高保证虚线垂直对齐
$row-min-height: 76px;

.goods-info {
  display: flex;
  align-items: center;
  padding: 8px 0;
  min-height: $row-min-height;
  box-sizing: border-box;
  border-bottom: 1px dashed #e5e5e5;

  &:last-child {
    border-bottom: none;
  }

  &__img {
    width: 50px;
    height: 50px;
    margin-right: 10px;
    object-fit: cover;
  }

  &__content {
    flex: 1;
  }

  &__name {
    font-size: 12px;
    line-height: 18px;
    color: #333;
  }

  &__spec {
    font-size: 12px;
    line-height: 18px;
    color: #999;
  }
}

.goods-qty,
.goods-amount {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 8px 0;
  min-height: $row-min-height;
  box-sizing: border-box;
  border-bottom: 1px dashed #e5e5e5;
  font-size: 12px;
  line-height: 20px;

  &:last-child {
    border-bottom: none;
  }
}

.status-orange {
  color: #fa8c16;
}

.status-red {
  color: #ab0033;
}

.status-green {
  color: #52c41a;
}
</style>
